// src/api/ventas.js - API para manejo de ventas
import { apiFetch } from './index.js';

/**
 * Obtener todas las ventas
 * @param {Object} params - Parámetros de consulta (opcional)
 * @returns {Promise} Lista de ventas
 */
export const obtenerVentas = async (params = {}) => {
  try {
    console.log('📋 Obteniendo todas las ventas...', params);
    
    let endpoint = '/api/Venta';
    
    if (Object.keys(params).length > 0) {
      const queryParams = new URLSearchParams(params).toString();
      endpoint += `?${queryParams}`;
    }
    
    const response = await apiFetch(endpoint);
    
    const data = Array.isArray(response) ? response : (response.data || []);
    const ventasNormalizadas = data.map(venta => ({
      idVenta: venta.idVenta,
      numeroFactura: venta.numeroFactura,
      fechaVenta: venta.fechaVenta,
      tipoVenta: venta.tipoVenta,
      idTipoCredito: venta.idTipoCredito,
      creditoAprobadoPor: venta.creditoAprobadoPor,
      creditoFechaAprobacion: venta.creditoFechaAprobacion,
      creditoObservaciones: venta.creditoObservaciones,
      subtotalUsd: venta.subtotalUsd || 0,
      ivaUsd: venta.ivaUsd || 0,
      descuentoUsd: venta.descuentoUsd || 0,
      totalUsd: venta.totalUsd || 0,
      subtotalBs: venta.subtotalBs || 0,
      ivaBs: venta.ivaBs || 0,
      descuentoBs: venta.descuentoBs || 0,
      totalBs: venta.totalBs || 0,
      costoTotalUsd: venta.costoTotalUsd || 0,
      gananciaNetaUsd: venta.gananciaNetaUsd || 0,
      margenGananciaPorcentaje: venta.margenGananciaPorcentaje || 0,
      tasaBsUsd: venta.tasaBsUsd || 0,
      idFuenteTasaVenta: venta.idFuenteTasaVenta,
      idTasaVenta: venta.idTasaVenta,
      idVendedor: venta.idVendedor,
      estado: venta.estado,
      observaciones: venta.observaciones,
      idCliente: venta.idCliente,
      clienteNombre: venta.clienteNombre,
      vendedorNombre: venta.vendedorNombre,
      fecha_creacion: venta.fecha_creacion || venta.fechaCreacion,
      productos: venta.productos,
      cantidadTotal: venta.cantidadTotal || 0,
      generaIva: venta.generaIva !== undefined ? venta.generaIva : true,
      tipoDocumento: venta.tipoDocumento || 'factura'
    }));
    
    return {
      success: true,
      data: ventasNormalizadas,
      count: ventasNormalizadas.length,
      message: 'Ventas obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo ventas:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener una venta por ID
 * @param {number} id - ID de la venta
 * @returns {Promise} Datos de la venta
 */
export const obtenerVentaPorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de venta es requerido');
    }
    
    console.log(`🔍 Obteniendo venta ID: ${id}`);
    
    const response = await apiFetch(`/api/Venta/${id}`);
    
    const data = response.data || response;
    
    return {
      success: true,
      data: {
        idVenta: data.idVenta,
        numeroFactura: data.numeroFactura,
        fechaVenta: data.fechaVenta,
        tipoVenta: data.tipoVenta,
        idTipoCredito: data.idTipoCredito,
        creditoAprobadoPor: data.creditoAprobadoPor,
        creditoFechaAprobacion: data.creditoFechaAprobacion,
        creditoObservaciones: data.creditoObservaciones,
        subtotalUsd: data.subtotalUsd || 0,
        ivaUsd: data.ivaUsd || 0,
        descuentoUsd: data.descuentoUsd || 0,
        totalUsd: data.totalUsd || 0,
        subtotalBs: data.subtotalBs || 0,
        ivaBs: data.ivaBs || 0,
        descuentoBs: data.descuentoBs || 0,
        totalBs: data.totalBs || 0,
        costoTotalUsd: data.costoTotalUsd || 0,
        gananciaNetaUsd: data.gananciaNetaUsd || 0,
        margenGananciaPorcentaje: data.margenGananciaPorcentaje || 0,
        tasaBsUsd: data.tasaBsUsd || 0,
        idFuenteTasaVenta: data.idFuenteTasaVenta,
        idTasaVenta: data.idTasaVenta,
        idVendedor: data.idVendedor,
        estado: data.estado,
        observaciones: data.observaciones,
        idCliente: data.idCliente,
        clienteNombre: data.clienteNombre,
        vendedorNombre: data.vendedorNombre,
        generaIva: data.generaIva !== undefined ? data.generaIva : true,
        tipoDocumento: data.tipoDocumento || 'factura'
      },
      message: response.message || 'Venta obtenida exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo venta ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener venta con relaciones completas
 * @param {number} id - ID de la venta
 * @returns {Promise} Datos de la venta con relaciones
 */
export const obtenerVentaConRelaciones = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de venta es requerido');
    }
    
    console.log(`🔍 Obteniendo venta con relaciones ID: ${id}`);
    
    const response = await apiFetch(`/api/Venta/con-relaciones/${id}`);
    
    const data = response.data || response;
    
    const detalles = (data.detalles || []).map(detalle => ({
      idVentaDetalle: detalle.idVentaDetalle,
      idProducto: detalle.idProducto,
      cantidad: detalle.cantidad,
      precioUnitarioUsd: detalle.precioUnitarioUsd || 0,
      subtotalUsd: detalle.subtotalUsd || 0,
      precioUnitarioBs: detalle.precioUnitarioBs || 0,
      subtotalBs: detalle.subtotalBs || 0,
      costoUnitarioUsd: detalle.costoUnitarioUsd || 0,
      costoTotalUsd: detalle.costoTotalUsd || 0,
      gananciaUnitarioUsd: detalle.gananciaUnitarioUsd || 0,
      gananciaTotalUsd: detalle.gananciaTotalUsd || 0,
      margenGananciaPorcentaje: detalle.margenGananciaPorcentaje || 0,
      garantiaMeses: detalle.garantiaMeses || 0,
      garantiaDias: detalle.garantiaDias || 0,
      garantiaInicio: detalle.garantiaInicio,
      garantiaFin: detalle.garantiaFin,
      tieneGarantia: detalle.tieneGarantia || false,
      nombreProducto: detalle.nombreProducto || detalle.productoNombre,
      productoCodigo: detalle.productoCodigo
    }));
    
    const cuotas = (data.cuotasCredito || []).map(cuota => ({
      idCreditoCuota: cuota.idCreditoCuota,
      numeroCuota: cuota.numeroCuota,
      fechaVencimiento: cuota.fechaVencimiento,
      montoUsd: cuota.montoUsd || 0,
      saldoUsd: cuota.saldoUsd || 0,
      montoBs: cuota.montoBs || 0,
      saldoBs: cuota.saldoBs || 0,
      estado: cuota.estado,
      fechaPago: cuota.fechaPago,
      observaciones: cuota.observaciones
    }));
    
    return {
      success: true,
      data: {
        ...data,
        detalles,
        cuotasCredito: cuotas
      },
      message: response.message || 'Venta obtenida exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo venta con relaciones ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener ventas por estado
 * @param {string} estado - Estado de la venta
 * @returns {Promise} Lista de ventas
 */
export const obtenerVentasPorEstado = async (estado) => {
  try {
    if (!estado) {
      throw new Error('Estado es requerido');
    }
    
    console.log(`📋 Obteniendo ventas con estado: ${estado}`);
    
    const response = await apiFetch(`/api/Venta/estado/${encodeURIComponent(estado)}`);
    
    const data = Array.isArray(response) ? response : (response.data || []);
    
    return {
      success: true,
      data,
      count: data.length,
      message: `Ventas con estado ${estado} obtenidas exitosamente`
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo ventas por estado ${estado}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener el siguiente número de factura disponible
 * @returns {Promise} Número de factura (ej. FR-6000)
 */
export const obtenerSiguienteNumeroFactura = async () => {
  try {
    console.log('📋 Obteniendo el próximo número de factura...');
    
    const response = await apiFetch('/api/Venta/next-factura');
    
    return {
      success: true,
      data: response.numeroFactura || response.data?.numeroFactura || 'FR-6000',
      message: 'Número de factura obtenido exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo próximo número de factura:', error);
    return {
      success: false,
      error: error.message,
      data: 'FR-6000'
    };
  }
};

/**
 * Obtener ventas por tipo
 * @param {string} tipoVenta - Tipo de venta (contado, credito)
 * @returns {Promise} Lista de ventas
 */
export const obtenerVentasPorTipo = async (tipoVenta) => {
  try {
    if (!tipoVenta) {
      throw new Error('Tipo de venta es requerido');
    }
    
    console.log(`📋 Obteniendo ventas tipo: ${tipoVenta}`);
    
    const response = await apiFetch(`/api/Venta/tipo/${encodeURIComponent(tipoVenta)}`);
    
    const data = Array.isArray(response) ? response : (response.data || []);
    
    return {
      success: true,
      data,
      count: data.length,
      message: `Ventas tipo ${tipoVenta} obtenidas exitosamente`
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo ventas por tipo ${tipoVenta}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener ventas por cliente
 * @param {number} idCliente - ID del cliente
 * @returns {Promise} Lista de ventas
 */
export const obtenerVentasPorCliente = async (idCliente) => {
  try {
    if (!idCliente) {
      throw new Error('ID de cliente es requerido');
    }
    
    console.log(`📋 Obteniendo ventas del cliente ID: ${idCliente}`);
    
    const response = await apiFetch(`/api/Venta/cliente/${idCliente}`);
    
    const data = Array.isArray(response) ? response : (response.data || []);
    
    return {
      success: true,
      data,
      count: data.length,
      message: 'Ventas del cliente obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo ventas del cliente ${idCliente}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener ventas por vendedor
 * @param {number} idVendedor - ID del vendedor
 * @returns {Promise} Lista de ventas
 */
export const obtenerVentasPorVendedor = async (idVendedor) => {
  try {
    if (!idVendedor) {
      throw new Error('ID de vendedor es requerido');
    }
    
    console.log(`📋 Obteniendo ventas del vendedor ID: ${idVendedor}`);
    
    const response = await apiFetch(`/api/Venta/vendedor/${idVendedor}`);
    
    const data = Array.isArray(response) ? response : (response.data || []);
    
    return {
      success: true,
      data,
      count: data.length,
      message: 'Ventas del vendedor obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo ventas del vendedor ${idVendedor}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener ventas por rango de fechas
 * @param {string} fechaInicio - Fecha inicial
 * @param {string} fechaFin - Fecha final
 * @returns {Promise} Lista de ventas
 */
export const obtenerVentasPorRangoFechas = async (fechaInicio, fechaFin) => {
  try {
    if (!fechaInicio || !fechaFin) {
      throw new Error('Fechas de inicio y fin son requeridas');
    }
    
    console.log(`📅 Obteniendo ventas entre ${fechaInicio} y ${fechaFin}`);
    
    const response = await apiFetch(`/api/Venta/fecha-rango?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}`);
    
    const data = Array.isArray(response) ? response : (response.data || []);
    
    return {
      success: true,
      data,
      count: data.length,
      message: 'Ventas en rango obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo ventas por rango de fechas:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener resumen de ventas
 * @returns {Promise} Resumen estadístico
 */
export const obtenerResumenVentas = async () => {
  try {
    console.log('📊 Obteniendo resumen de ventas...');
    
    const response = await apiFetch('/api/Venta/resumen');
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Resumen obtenido exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo resumen de ventas:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener créditos pendientes
 * @returns {Promise} Lista de créditos pendientes
 */
export const obtenerCreditosPendientes = async () => {
  try {
    console.log('📋 Obteniendo créditos pendientes...');
    
    const response = await apiFetch('/api/Venta/credito-pendientes');
    
    const data = Array.isArray(response) ? response : (response.data || []);
    
    return {
      success: true,
      data,
      count: data.length,
      message: 'Créditos pendientes obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo créditos pendientes:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener pagos de una venta
 * @param {number} idVenta - ID de la venta
 * @returns {Promise} Lista de pagos
 */
export const obtenerPagosDeVenta = async (idVenta) => {
  try {
    if (!idVenta) {
      throw new Error('ID de venta es requerido');
    }
    
    console.log(`💰 Obteniendo pagos de venta ID: ${idVenta}`);
    
    const response = await apiFetch(`/api/Venta/${idVenta}/pagos`);
    
    const data = Array.isArray(response) ? response : (response.data || []);
    
    return {
      success: true,
      data,
      count: data.length,
      message: 'Pagos de venta obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo pagos de venta ${idVenta}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Buscar ventas por término
 * @param {string} termino - Término de búsqueda
 * @returns {Promise} Lista de ventas
 */
export const buscarVentasAPI = async (termino) => {
  try {
    if (!termino || termino.trim() === '') {
      throw new Error('Término de búsqueda es requerido');
    }
    
    console.log(`🔎 Buscando ventas: "${termino}"`);
    
    const response = await apiFetch('/api/Venta/search', {
      method: 'POST',
      body: JSON.stringify({ termino: termino.trim() })
    });
    
    const data = response.data || response;
    
    return {
      success: true,
      data: Array.isArray(data) ? data : [],
      count: Array.isArray(data) ? data.length : 0,
      message: response.message || 'Búsqueda completada'
    };
    
  } catch (error) {
    console.error(`❌ Error buscando ventas "${termino}":`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Crear una nueva venta (genérica)
 * @param {Object} ventaData - Datos de la venta
 * @returns {Promise} Venta creada
 */
export const crearVenta = async (ventaData) => {
  try {
    if (!ventaData.idCliente) {
      throw new Error('ID de cliente es requerido');
    }
    
    if (!ventaData.detalles || ventaData.detalles.length === 0) {
      throw new Error('Se requiere al menos un detalle de venta');
    }
    
    if (!ventaData.tipoVenta) {
      throw new Error('Tipo de venta es requerido');
    }
    
    console.log('➕ Creando nueva venta:', ventaData);
    
    const detallesFormateados = ventaData.detalles.map(detalle => ({
      idProducto: detalle.idProducto || null,
      idServicio: detalle.idServicio || null,
      idTecnico: detalle.idTecnico || null,
      cantidad: parseInt(detalle.cantidad),
      precioUnitarioUsd: parseFloat(detalle.precioUnitarioUsd) || 0,
      subtotalUsd: parseFloat(detalle.subtotalUsd) || 0,
      precioUnitarioBs: parseFloat(detalle.precioUnitarioBs) || 0,
      subtotalBs: parseFloat(detalle.subtotalBs) || 0,
      costoUnitarioUsd: parseFloat(detalle.costoUnitarioUsd) || 0,
      costoTotalUsd: parseFloat(detalle.costoTotalUsd) || 0,
      gananciaUnitarioUsd: parseFloat(detalle.gananciaUnitarioUsd) || 0,
      gananciaTotalUsd: parseFloat(detalle.gananciaTotalUsd) || 0,
      margenGananciaPorcentaje: parseFloat(detalle.margenGananciaPorcentaje) || 0,
      garantiaMeses: parseInt(detalle.garantiaMeses) || 0,
      garantiaDias: parseInt(detalle.garantiaDias) || 0,
      garantiaInicio: detalle.garantiaInicio || null,
      garantiaFin: detalle.garantiaFin || null,
      tieneGarantia: detalle.tieneGarantia || false
    }));
    
    let cuotasFormateadas = null;
    if (ventaData.tipoVenta === 'credito' && ventaData.cuotasCredito) {
      cuotasFormateadas = ventaData.cuotasCredito.map(cuota => ({
        numeroCuota: parseInt(cuota.numeroCuota),
        fechaVencimiento: cuota.fechaVencimiento,
        montoUsd: parseFloat(cuota.montoUsd) || 0,
        saldoUsd: parseFloat(cuota.saldoUsd) || parseFloat(cuota.montoUsd) || 0,
        montoBs: parseFloat(cuota.montoBs) || 0,
        saldoBs: parseFloat(cuota.saldoBs) || parseFloat(cuota.montoBs) || 0,
        interesMoraUsd: parseFloat(cuota.interesMoraUsd) || 0,
        interesMoraBs: parseFloat(cuota.interesMoraBs) || 0,
        estado: cuota.estado || 'pendiente',
        observaciones: cuota.observaciones || null
      }));
    }
    
    const payload = {
      numeroFactura: ventaData.numeroFactura || null,
      fechaVenta: ventaData.fechaVenta || new Date().toISOString(),
      tipoVenta: ventaData.tipoVenta,
      idTipoCredito: ventaData.idTipoCredito || null,
      creditoAprobadoPor: ventaData.creditoAprobadoPor || null,
      creditoFechaAprobacion: ventaData.creditoFechaAprobacion || null,
      creditoObservaciones: ventaData.creditoObservaciones || null,
      subtotalUsd: parseFloat(ventaData.subtotalUsd) || 0,
      ivaUsd: parseFloat(ventaData.ivaUsd) || 0,
      descuentoUsd: parseFloat(ventaData.descuentoUsd) || 0,
      totalUsd: parseFloat(ventaData.totalUsd) || 0,
      subtotalBs: parseFloat(ventaData.subtotalBs) || 0,
      ivaBs: parseFloat(ventaData.ivaBs) || 0,
      descuentoBs: parseFloat(ventaData.descuentoBs) || 0,
      totalBs: parseFloat(ventaData.totalBs) || 0,
      costoTotalUsd: parseFloat(ventaData.costoTotalUsd) || 0,
      gananciaNetaUsd: parseFloat(ventaData.gananciaNetaUsd) || 0,
      margenGananciaPorcentaje: parseFloat(ventaData.margenGananciaPorcentaje) || 0,
      tasaBsUsd: parseFloat(ventaData.tasaBsUsd) || 0,
      idFuenteTasaVenta: ventaData.idFuenteTasaVenta || 1,
      idTasaVenta: ventaData.idTasaVenta || null,
      idVendedor: ventaData.idVendedor || null,
      idUsuario: ventaData.idUsuario || 1,
      estado: ventaData.estado || 'completada',
      observaciones: ventaData.observaciones || null,
      idCliente: ventaData.idCliente,
      // ⭐ CRÍTICO: estos dos campos determinan si se imprime fiscalmente o no
      generaIva: ventaData.generaIva ?? true,
      tipoDocumento: ventaData.tipoDocumento || 'factura',
      detalles: detallesFormateados,
      cuotasCredito: cuotasFormateadas
    };

    console.log('📦 Payload enviado a /api/Venta:', JSON.stringify(payload, null, 2));
    
    const response = await apiFetch('/api/Venta', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Venta creada exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error creando venta:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * CREAR VENTA A CRÉDITO - Función específica para ventas a crédito
 */
export const crearVentaCredito = async (ventaData) => {
  try {
    console.log('➕ Creando venta a crédito:', ventaData);
    
    if (!ventaData.idCliente) {
      throw new Error('ID de cliente es requerido');
    }
    
    if (!ventaData.detalles || ventaData.detalles.length === 0) {
      throw new Error('Se requiere al menos un detalle de venta');
    }
    
    if (!ventaData.subtipoCredito) {
      throw new Error('Subtipo de crédito es requerido (cacheas o empresa)');
    }
    
    const idTipoCredito = ventaData.subtipoCredito === 'cacheas' ? 'CAS-001' : 'EMP-001';
    
    const subtotalUsd = Number(ventaData.subtotalOriginalUsd) || 0;
    const descuentoUsd = Number(ventaData.descuentoUsd) || 0;
    const totalUsd = Number(ventaData.totalConRecargoUsd) || subtotalUsd;
    const tasaBsUsd = Number(ventaData.tasaBsUsd) || 0;
    
    const subtotalBs = Number((subtotalUsd * tasaBsUsd).toFixed(2));
    const descuentoBs = Number((descuentoUsd * tasaBsUsd).toFixed(2));
    const totalBs = Number((totalUsd * tasaBsUsd).toFixed(2));
    
    const costoTotalUsd = Number(ventaData.costoTotalUsd) || 0;
    const gananciaNetaUsd = Number((totalUsd - costoTotalUsd).toFixed(2));
    const margenGananciaPorcentaje = costoTotalUsd > 0 
      ? Number(((gananciaNetaUsd / costoTotalUsd) * 100).toFixed(2))
      : (totalUsd > 0 ? 100 : 0);
    
    const detallesFormateados = ventaData.detalles.map(detalle => {
      const precioUnitarioBs = Number((detalle.precioUnitarioUsd * tasaBsUsd).toFixed(2));
      const subtotalBsDetalle = Number((detalle.subtotalUsd * tasaBsUsd).toFixed(2));
      const costoTotalUsdDetalle = Number((detalle.cantidad * (detalle.costoUnitarioUsd || 0)).toFixed(2));
      const gananciaUnitarioUsd = detalle.precioUnitarioUsd - (detalle.costoUnitarioUsd || 0);
      const gananciaTotalUsd = Number((detalle.subtotalUsd - costoTotalUsdDetalle).toFixed(2));
      const margenGananciaPorcentajeDetalle = detalle.precioUnitarioUsd > 0 
        ? Number(((gananciaUnitarioUsd / detalle.precioUnitarioUsd) * 100).toFixed(2))
        : 0;
      
      const fechaInicioGarantia = new Date().toISOString().split('T')[0];
      const fechaFinGarantia = new Date();
      fechaFinGarantia.setMonth(fechaFinGarantia.getMonth() + (detalle.garantiaMeses || 0));
      fechaFinGarantia.setDate(fechaFinGarantia.getDate() + (detalle.garantiaDias || 0));
      
      return {
        idProducto: detalle.idProducto || null,
        idServicio: detalle.idServicio || null,
        idTecnico: detalle.idTecnico || null,
        cantidad: detalle.cantidad,
        precioUnitarioUsd: detalle.precioUnitarioUsd,
        subtotalUsd: detalle.subtotalUsd,
        precioUnitarioBs: precioUnitarioBs,
        subtotalBs: subtotalBsDetalle,
        costoUnitarioUsd: detalle.costoUnitarioUsd || 0,
        costoTotalUsd: costoTotalUsdDetalle,
        gananciaUnitarioUsd: gananciaUnitarioUsd,
        gananciaTotalUsd: gananciaTotalUsd,
        margenGananciaPorcentaje: margenGananciaPorcentajeDetalle,
        garantiaMeses: detalle.garantiaMeses || 0,
        garantiaDias: detalle.garantiaDias || 0,
        garantiaInicio: fechaInicioGarantia,
        garantiaFin: fechaFinGarantia.toISOString().split('T')[0],
        tieneGarantia: (detalle.garantiaMeses > 0 || detalle.garantiaDias > 0)
      };
    });
    
    let cuotasFormateadas = null;
    if (ventaData.subtipoCredito === 'cacheas' && ventaData.cuotas && ventaData.cuotas.lista) {
      cuotasFormateadas = ventaData.cuotas.lista.map((cuota, index) => ({
        codigoTipoCredito: 'CAS-001',
        numeroCuota: index + 1,
        fechaVencimiento: cuota.fechaPago,
        montoUsd: parseFloat(cuota.montoUsd) || 0,
        saldoUsd: parseFloat(cuota.montoUsd) || 0,
        montoBs: parseFloat(cuota.montoBs) || 0,
        saldoBs: parseFloat(cuota.montoBs) || 0,
        interesMoraUsd: 0,
        interesMoraBs: 0,
        estado: 'pendiente',
        fechaPago: null,
        observaciones: cuota.observaciones || null
      }));
    } else if (ventaData.subtipoCredito === 'empresa') {
      const saldoPendienteUsd = Math.max(0, totalUsd - (ventaData.montoInicial || 0));
      if (saldoPendienteUsd > 0) {
        const saldoPendienteBs = Number((saldoPendienteUsd * tasaBsUsd).toFixed(2));
        
        let fechaVencimiento = new Date();
        if (ventaData.creditoEmpresa && ventaData.creditoEmpresa.fechaPagoAcordada) {
          fechaVencimiento = new Date(ventaData.creditoEmpresa.fechaPagoAcordada);
        } else {
          fechaVencimiento.setDate(fechaVencimiento.getDate() + 15);
        }
        
        cuotasFormateadas = [{
          codigoTipoCredito: 'EMP-001',
          numeroCuota: 1,
          fechaVencimiento: fechaVencimiento.toISOString().split('T')[0],
          montoUsd: saldoPendienteUsd,
          saldoUsd: saldoPendienteUsd,
          montoBs: saldoPendienteBs,
          saldoBs: saldoPendienteBs,
          interesMoraUsd: 0,
          interesMoraBs: 0,
          estado: 'pendiente',
          fechaPago: null,
          observaciones: ventaData.creditoEmpresa?.observaciones || `Crédito Empresa - Factura ${ventaData.numeroFactura}`
        }];
      }
    }
    
    const idFuenteTasaVenta = ventaData.idFuenteTasaVenta || null;
    
    const payload = {
      numeroFactura: ventaData.numeroFactura,
      fechaVenta: ventaData.fechaVenta || new Date().toISOString(),
      tipoVenta: 'credito',
      idTipoCredito: idTipoCredito,
      creditoAprobadoPor: null,
      creditoFechaAprobacion: null,
      creditoObservaciones: ventaData.observaciones || null,
      subtotalUsd: subtotalUsd,
      ivaUsd: 0,
      descuentoUsd: descuentoUsd,
      totalUsd: totalUsd,
      subtotalBs: subtotalBs,
      ivaBs: 0,
      descuentoBs: descuentoBs,
      totalBs: totalBs,
      costoTotalUsd: costoTotalUsd,
      gananciaNetaUsd: gananciaNetaUsd,
      margenGananciaPorcentaje: margenGananciaPorcentaje,
      tasaBsUsd: tasaBsUsd,
      idFuenteTasaVenta: idFuenteTasaVenta,
      idTasaVenta: ventaData.idTasaVenta,
      idVendedor: ventaData.idVendedor,
      idUsuario: ventaData.idUsuario || 1,
      estado: 'Activa',
      observaciones: ventaData.observaciones || null,
      idCliente: ventaData.idCliente,
      // ⭐ Los créditos NO llevan IVA → NO se imprimen fiscalmente
      generaIva: false,
      tipoDocumento: 'nota_credito',
      detalles: detallesFormateados,
      cuotasCredito: cuotasFormateadas
    };
    
    console.log('📦 Payload para crear venta a crédito:', JSON.stringify(payload, null, 2));
    
    const response = await apiFetch('/api/Venta', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Venta a crédito creada exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error creando venta a crédito:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Actualizar una venta existente
 */
export const actualizarVenta = async (id, ventaData) => {
  try {
    if (!id) {
      throw new Error('ID de venta es requerido');
    }
    
    console.log(`✏️ Actualizando venta ID: ${id}`, ventaData);
    
    const payload = {
      idVenta: parseInt(id),
      numeroFactura: ventaData.numeroFactura,
      fechaVenta: ventaData.fechaVenta,
      tipoVenta: ventaData.tipoVenta,
      idTipoCredito: ventaData.idTipoCredito,
      creditoAprobadoPor: ventaData.creditoAprobadoPor,
      creditoFechaAprobacion: ventaData.creditoFechaAprobacion,
      creditoObservaciones: ventaData.creditoObservaciones,
      subtotalUsd: parseFloat(ventaData.subtotalUsd) || 0,
      ivaUsd: parseFloat(ventaData.ivaUsd) || 0,
      descuentoUsd: parseFloat(ventaData.descuentoUsd) || 0,
      totalUsd: parseFloat(ventaData.totalUsd) || 0,
      subtotalBs: parseFloat(ventaData.subtotalBs) || 0,
      ivaBs: parseFloat(ventaData.ivaBs) || 0,
      descuentoBs: parseFloat(ventaData.descuentoBs) || 0,
      totalBs: parseFloat(ventaData.totalBs) || 0,
      costoTotalUsd: parseFloat(ventaData.costoTotalUsd) || 0,
      gananciaNetaUsd: parseFloat(ventaData.gananciaNetaUsd) || 0,
      margenGananciaPorcentaje: parseFloat(ventaData.margenGananciaPorcentaje) || 0,
      tasaBsUsd: parseFloat(ventaData.tasaBsUsd) || 0,
      idFuenteTasaVenta: ventaData.idFuenteTasaVenta || 0,
      idTasaVenta: ventaData.idTasaVenta,
      idVendedor: ventaData.idVendedor,
      estado: ventaData.estado,
      observaciones: ventaData.observaciones,
      idCliente: ventaData.idCliente,
      generaIva: ventaData.generaIva ?? true,
      tipoDocumento: ventaData.tipoDocumento || 'factura'
    };
    
    const response = await apiFetch(`/api/Venta/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Venta actualizada exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error actualizando venta ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Anular una venta
 */
export const anularVenta = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de venta es requerido');
    }
    
    console.log(`🚫 Anulando venta ID: ${id}`);
    
    const response = await apiFetch(`/api/Venta/${id}/anular`, {
      method: 'PATCH'
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Venta anulada exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error anulando venta ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Registrar pago de venta
 */
export const registrarPagoVenta = async (pagoData) => {
  try {
    if (!pagoData.idVenta) {
      throw new Error('ID de venta es requerido');
    }
    
    if (!pagoData.montoUsd && !pagoData.montoBs) {
      throw new Error('Se requiere al menos un monto (USD o Bs)');
    }
    
    console.log(`💰 Registrando pago para venta ID: ${pagoData.idVenta}`, pagoData);
    
    const response = await apiFetch('/api/Venta/pago', {
      method: 'POST',
      body: JSON.stringify({
        idVenta: pagoData.idVenta,
        montoUsd: parseFloat(pagoData.montoUsd) || 0,
        montoBs: parseFloat(pagoData.montoBs) || 0,
        fechaPago: pagoData.fechaPago || new Date().toISOString(),
        tipoPago: pagoData.tipoPago,
        numeroRecibo: pagoData.numeroRecibo || null,
        observaciones: pagoData.observaciones || null
      })
    });
    
    if (pagoData.idCuota) {
        await apiFetch('/api/CreditoCuota/pagar', {
            method: 'POST',
            body: JSON.stringify({
                idCuota: pagoData.idCuota,
                montoPagoUsd: parseFloat(pagoData.montoUsd) || 0,
                montoPagoBs: parseFloat(pagoData.montoBs) || 0,
                fechaPago: pagoData.fechaPago || new Date().toISOString(),
                observaciones: pagoData.referencia || "Pago de cuota"
            })
        });
    }
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Pago registrado exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error registrando pago:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

// ==================== FUNCIONES AUXILIARES ====================

export const buscarVentasLocal = (ventas, termino) => {
  if (!termino || termino.trim() === '') return ventas;
  
  const terminoLower = termino.toLowerCase();
  return ventas.filter(venta => 
    venta.numeroFactura?.toLowerCase().includes(terminoLower) ||
    venta.clienteNombre?.toLowerCase().includes(terminoLower) ||
    venta.vendedorNombre?.toLowerCase().includes(terminoLower) ||
    venta.tipoVenta?.toLowerCase().includes(terminoLower) ||
    venta.estado?.toLowerCase().includes(terminoLower) ||
    venta.idVenta?.toString().includes(terminoLower)
  );
};

export const filtrarVentasPorEstadoLocal = (ventas, estado) => {
  if (!estado || estado === 'all') return ventas;
  return ventas.filter(v => v.estado?.toLowerCase() === estado.toLowerCase());
};

export const filtrarVentasPorTipoLocal = (ventas, tipo) => {
  if (!tipo || tipo === 'all') return ventas;
  return ventas.filter(v => v.tipoVenta?.toLowerCase() === tipo.toLowerCase());
};

export const filtrarVentasPorFechaLocal = (ventas, fechaInicio, fechaFin) => {
  if (!fechaInicio && !fechaFin) return ventas;
  
  return ventas.filter(venta => {
    const fechaVenta = new Date(venta.fechaVenta);
    if (fechaInicio && fechaFin) {
      return fechaVenta >= fechaInicio && fechaVenta <= fechaFin;
    } else if (fechaInicio) {
      return fechaVenta >= fechaInicio;
    } else if (fechaFin) {
      return fechaVenta <= fechaFin;
    }
    return true;
  });
};

export const obtenerEstadisticasVentas = (ventas) => {
  const totalUsd = ventas.reduce((sum, v) => sum + (v.totalUsd || 0), 0);
  const totalBs = ventas.reduce((sum, v) => sum + (v.totalBs || 0), 0);
  const totalCostoUsd = ventas.reduce((sum, v) => sum + (v.costoTotalUsd || 0), 0);
  const totalGananciaUsd = ventas.reduce((sum, v) => sum + (v.gananciaNetaUsd || 0), 0);
  
  const margenPromedio = ventas.length > 0 
    ? ventas.reduce((sum, v) => sum + (v.margenGananciaPorcentaje || 0), 0) / ventas.length
    : 0;
  
  const contado = ventas.filter(v => v.tipoVenta?.toLowerCase() === 'contado').length;
  const credito = ventas.filter(v => v.tipoVenta?.toLowerCase() === 'credito').length;
  const completadas = ventas.filter(v => v.estado?.toLowerCase() === 'completada').length;
  const pendientes = ventas.filter(v => v.estado?.toLowerCase() === 'pendiente').length;
  const anuladas = ventas.filter(v => v.estado?.toLowerCase() === 'anulada').length;
  const ticketPromedioUsd = ventas.length > 0 ? totalUsd / ventas.length : 0;
  const ticketPromedioBs = ventas.length > 0 ? totalBs / ventas.length : 0;
  
  return {
    total: ventas.length,
    totalUsd,
    totalBs,
    totalCostoUsd,
    totalGananciaUsd,
    margenPromedio: margenPromedio.toFixed(2),
    porTipo: { contado, credito },
    porEstado: { completadas, pendientes, anuladas },
    ticketPromedioUsd,
    ticketPromedioBs,
    rentabilidad: totalCostoUsd > 0 ? (totalGananciaUsd / totalCostoUsd) * 100 : 0
  };
};

export const ordenarVentas = (ventas, criterio = 'fecha_desc') => {
  const ventasCopy = [...ventas];
  
  switch (criterio) {
    case 'fecha_desc':
      return ventasCopy.sort((a, b) => new Date(b.fechaVenta) - new Date(a.fechaVenta));
    case 'fecha':
      return ventasCopy.sort((a, b) => new Date(a.fechaVenta) - new Date(b.fechaVenta));
    case 'totalUsd_desc':
      return ventasCopy.sort((a, b) => (b.totalUsd || 0) - (a.totalUsd || 0));
    case 'totalUsd':
      return ventasCopy.sort((a, b) => (a.totalUsd || 0) - (b.totalUsd || 0));
    case 'cliente':
      return ventasCopy.sort((a, b) => (a.clienteNombre || '').localeCompare(b.clienteNombre || ''));
    case 'vendedor':
      return ventasCopy.sort((a, b) => (a.vendedorNombre || '').localeCompare(b.vendedorNombre || ''));
    default:
      return ventasCopy;
  }
};

export const formatearFechaVenta = (fechaISO, completo = false) => {
  if (!fechaISO) return 'N/A';
  
  try {
    const fecha = new Date(fechaISO);
    const opciones = { year: 'numeric', month: 'short', day: 'numeric' };
    if (completo) opciones.hour = '2-digit', opciones.minute = '2-digit';
    return fecha.toLocaleDateString('es-ES', opciones);
  } catch {
    return fechaISO;
  }
};

export const formatearTipoVenta = (tipo) => {
  if (!tipo) return 'No especificado';
  const tipos = { 'contado': 'Contado', 'credito': 'Crédito' };
  return tipos[tipo.toLowerCase()] || tipo;
};

export const formatearEstadoVenta = (estado) => {
  if (!estado) return 'No especificado';
  const estados = { 'completada': 'Completada', 'pendiente': 'Pendiente', 'anulada': 'Anulada', 'Activa': 'Activa' };
  return estados[estado.toLowerCase()] || estado;
};

export const getColorPorEstadoVenta = (estado) => {
  if (!estado) return 'bg-gray-100 text-gray-800';
  const colores = {
    'completada': 'bg-green-100 text-green-800',
    'Activa': 'bg-green-100 text-green-800',
    'pendiente': 'bg-yellow-100 text-yellow-800',
    'anulada': 'bg-red-100 text-red-800'
  };
  return colores[estado.toLowerCase()] || 'bg-gray-100 text-gray-800';
};

export const getColorPorTipoVenta = (tipo) => {
  if (!tipo) return 'bg-gray-100 text-gray-800';
  const colores = { 'contado': 'bg-blue-100 text-blue-800', 'credito': 'bg-purple-100 text-purple-800' };
  return colores[tipo.toLowerCase()] || 'bg-gray-100 text-gray-800';
};

export const exportarVentasExcel = (ventas, filename = 'ventas') => {
  try {
    if (typeof window === 'undefined') {
      return { success: false, error: 'Exportación solo disponible en el navegador', filename: null };
    }
    
    const XLSX = require('xlsx');
    const wb = XLSX.utils.book_new();
    
    const data = ventas.map(venta => ({
      'ID Venta': venta.idVenta || '',
      'N° Factura': venta.numeroFactura || '',
      'Fecha': formatearFechaVenta(venta.fechaVenta, true),
      'Cliente': venta.clienteNombre || '',
      'Vendedor': venta.vendedorNombre || '',
      'Tipo': formatearTipoVenta(venta.tipoVenta),
      'Total USD': venta.totalUsd || 0,
      'Total Bs': venta.totalBs || 0,
      'Estado': formatearEstadoVenta(venta.estado)
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Ventas');
    
    const fecha = new Date();
    const nombreArchivo = `${filename}_${fecha.toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, nombreArchivo);
    
    return { success: true, filename: nombreArchivo, message: 'Archivo generado', count: ventas.length };
  } catch (error) {
    console.error('❌ Error exportando a Excel:', error);
    return { success: false, error: error.message, filename: null };
  }
};

/**
 * Eliminar una venta
 */
export const eliminarVenta = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de venta es requerido');
    }
    
    console.log(`🗑️ Eliminando venta ID: ${id}`);
    
    const response = await apiFetch(`/api/Venta/${id}`, {
      method: 'DELETE'
    });
    
    return {
      success: true,
      data: response,
      message: 'Venta eliminada exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error eliminando venta ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

// ==================== EXPORT DEFAULT ====================

export default {
  obtenerVentas,
  obtenerVentaPorId,
  obtenerVentaConRelaciones,
  obtenerVentasPorEstado,
  obtenerVentasPorTipo,
  obtenerVentasPorCliente,
  obtenerVentasPorVendedor,
  obtenerVentasPorRangoFechas,
  obtenerResumenVentas,
  obtenerCreditosPendientes,
  obtenerPagosDeVenta,
  buscarVentasAPI,
  crearVenta,
  crearVentaCredito,
  actualizarVenta,
  anularVenta,
  eliminarVenta,
  registrarPagoVenta,
  buscarVentasLocal,
  filtrarVentasPorEstadoLocal,
  filtrarVentasPorTipoLocal,
  filtrarVentasPorFechaLocal,
  obtenerEstadisticasVentas,
  ordenarVentas,
  formatearFechaVenta,
  formatearTipoVenta,
  formatearEstadoVenta,
  getColorPorEstadoVenta,
  getColorPorTipoVenta,
  exportarVentasExcel,
  obtenerSiguienteNumeroFactura
};